import React, { useState, useEffect, useRef } from 'react';
import { Link, navigate } from 'gatsby';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import styled from 'styled-components';
import { sr, srConfig } from '@utils';
import { usePrefersReducedMotion } from '@hooks';
import { useLanguage } from '@hooks/useLanguage';
import { fetchPosts } from '@utils/api';
import BlogCard from '@components/BlogCard';

const StyledProjectsSection = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;

  h2 {
    font-size: clamp(24px, 5vw, var(--fz-heading));
  }

  .MyBlog-link {
    font-family: var(--font-mono);
    font-size: var(--fz-sm);
    &:after {
      bottom: 0.1em;
    }
  }

  .projects-grid {
    ${({ theme }) => theme.mixins.resetList};
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    grid-gap: 15px;
    position: relative;
    margin-top: 50px;
    width: 100%; /* 确保网格占满容器宽度 */

    @media (max-width: 1080px) {
      grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    }

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
      grid-gap: 20px;
    }

    /* 确保所有网格项目宽度一致 */
    > * {
      width: 100%;
      min-width: 0; /* 允许内容收缩 */
    }
  }

  .more-button {
    ${({ theme }) => theme.mixins.button};
    margin: 80px auto 0;
  }

  .loading-text {
    color: var(--light-slate);
    font-family: var(--font-mono);
    font-size: var(--fz-sm);
    margin-top: 50px;
  }

  .error-text {
    color: var(--red);
    font-family: var(--font-mono);
    font-size: var(--fz-sm);
    margin-top: 50px;
  }

  .project-enter {
    opacity: 0;
    transform: translateY(20px);
  }
  .project-enter-active {
    opacity: 1;
    transform: translateY(0px);
    transition: opacity 300ms, transform 300ms;
  }
  .project-exit {
    opacity: 1;
  }
  .project-exit-active {
    opacity: 0;
    transform: translateY(-20px);
    transition: opacity 300ms, transform 300ms;
  }
`;

/**
 * Projects 组件 - 展示 My Blog 区域
 * 从 API 获取文章数据并展示前三条
 */
const Projects = () => {
  const { t } = useLanguage();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showingMore, setShowingMore] = useState(false);
  const revealTitle = useRef(null);
  const revealArchiveLink = useRef(null);
  const revealProjects = useRef([]);
  const nodeRefs = useRef({});
  const prefersReducedMotion = usePrefersReducedMotion();

  // 获取文章数据
  useEffect(() => {
    const loadPosts = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchPosts(1, 6); // 获取6条数据，前3条显示，后3条用于 Show More
        // eslint-disable-next-line no-console
        console.log('Received data:', data);

        // API返回格式: {success: true, data: {posts: [...], pagination: {...}}}
        if (data && data.success && data.data && Array.isArray(data.data.posts)) {
          setPosts(data.data.posts);
        } else if (Array.isArray(data)) {
          // 兼容直接返回数组的情况
          setPosts(data);
        } else if (data && data.posts) {
          // 兼容直接返回包含posts属性的对象
          setPosts(data.posts);
        } else {
          console.warn('Unexpected data format:', data);
          setPosts([]);
        }
      } catch (err) {
        setError('Failed to load blog posts');
        console.error('Error loading posts:', err);
      } finally {
        setLoading(false);
      }
    };

    loadPosts();
  }, []);

  // ScrollReveal 动画 - 使用 useRef 避免 findDOMNode 警告
  useEffect(() => {
    if (prefersReducedMotion || !sr) {
      return;
    }

    // 延迟执行以确保 DOM 元素已渲染
    const timer = setTimeout(() => {
      if (revealTitle.current && sr) {
        sr.reveal(revealTitle.current, srConfig());
      }
      if (revealArchiveLink.current && sr) {
        sr.reveal(revealArchiveLink.current, srConfig());
      }
      revealProjects.current.forEach((ref, i) => {
        if (ref && sr) {
          sr.reveal(ref, srConfig(i * 100));
        }
      });
    }, 100);

    return () => clearTimeout(timer);
  }, [prefersReducedMotion, posts]);

  /**
   * 处理 Read Now 点击事件
   * @param {Object} post - 文章数据
   */
  const handleReadNow = post => {
    // 跳转到 blog 页面，并传递文章 ID
    navigate(`/blog?postId=${post.id}`);
  };

  /**
   * 处理 Show More/Show Less 按钮点击
   */
  const handleShowMore = () => {
    setShowingMore(!showingMore);
  };

  // 决定显示的文章数量
  const displayedPosts = showingMore ? posts : posts.slice(0, 3);

  return (
    <StyledProjectsSection>
      <h2 ref={revealTitle}>My Blog</h2>

      <Link className="inline-link MyBlog-link" to="/blog" ref={revealArchiveLink}>
        {t('viewMyBlog')}
      </Link>

      {loading && <div className="loading-text">{t('updating')}</div>}

      {error && <div className="error-text">{error}</div>}

      {!loading && !error && posts.length > 0 && (
        <>
          <ul className="projects-grid">
            <TransitionGroup component={null}>
              {displayedPosts.map((post, i) => {
                // 为每个文章项创建稳定的 ref
                if (!nodeRefs.current[post.id]) {
                  nodeRefs.current[post.id] = React.createRef();
                }

                return (
                  <CSSTransition
                    key={post.id}
                    classNames="project"
                    timeout={300}
                    exit={!prefersReducedMotion}
                    nodeRef={nodeRefs.current[post.id]}>
                    <li
                      ref={el => {
                        revealProjects.current[i] = el;
                        if (nodeRefs.current[post.id]) {
                          nodeRefs.current[post.id].current = el;
                        }
                      }}
                      style={{
                        transitionDelay: `${i * 100}ms`,
                      }}>
                      <BlogCard post={post} onReadNow={handleReadNow} />
                    </li>
                  </CSSTransition>
                );
              })}
            </TransitionGroup>
          </ul>

          {posts.length > 3 && (
            <button className="more-button" onClick={handleShowMore}>
              {showingMore ? t('showLess') : t('showMore')}
            </button>
          )}
        </>
      )}

      {!loading && !error && posts.length === 0 && (
        <div className="loading-text">No blog posts available</div>
      )}
    </StyledProjectsSection>
  );
};

export default Projects;
